import { Reveal } from "@/components/Reveal";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const chapters = [
  {
    n: "01",
    title: "Outcomes over demos",
    body: "Anyone can ship a flashy prototype. We engineer agents that survive real traffic, edge cases, and audits — measured against business metrics, not vibes.",
  },
  {
    n: "02",
    title: "Autonomy with guardrails",
    body: "True agency means acting without hand-holding. We pair that freedom with evals, observability, and human-in-the-loop controls so trust scales with capability.",
  },
  {
    n: "03",
    title: "Own the whole stack",
    body: "Strategy, data, models, infra, and interface — fragmentation kills AI projects. One accountable team carries your idea from thesis to production.",
  },
  {
    n: "04",
    title: "Compound, don't rebuild",
    body: "Every engagement leaves you with reusable components, documented systems, and a team that levels up — so your next agent ships faster than the last.",
  },
];

function Chapter({ c, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [8, 0, -8]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.8, 1], [0.3, 1, 1, 0.3]);

  return (
    <motion.div
      ref={ref}
      style={{ rotateX, opacity, transformPerspective: 1200 }}
      className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-white/10"
      data-testid={`manifesto-chapter-${i}`}
    >
      <div className="md:col-span-4">
        <span className="font-display font-900 tracking-tighter text-7xl lg:text-8xl text-transparent"
          style={{ WebkitTextStroke: "1px rgba(255,255,255,0.22)" }}>
          {c.n}
        </span>
      </div>
      <div className="md:col-span-8 md:pt-4">
        <h3 className="font-display text-2xl sm:text-3xl font-500 text-white tracking-tight mb-4">{c.title}</h3>
        <p className="text-white/55 leading-relaxed max-w-xl">{c.body}</p>
      </div>
    </motion.div>
  );
}

export default function Manifesto() {
  return (
    <section id="approach" className="relative bg-[#050505] py-24 lg:py-32 border-t border-white/10" data-testid="manifesto-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
            <span className="overline text-[#8b7cff]/90">04 — Our Manifesto</span>
          </div>
          <h2 className="font-display font-700 tracking-tighter leading-none text-white text-4xl sm:text-5xl max-w-3xl mb-14">
            Four principles behind every system we ship.
          </h2>
        </Reveal>
        <div>
          {chapters.map((c, i) => (
            <Chapter key={c.n} c={c} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
